import { CustomEventType, type MFAResultEventDetails } from '../tokens/event';
import type { FetchError, RequestOptions } from './http.types';

export const MFA_REQUIRED_HEADER = 'www-authenticate';
export const MFA_SESSION_HEADER = 'X-MFA-Session-ID'.toLowerCase();
export const MFA_SESSION_STORAGE_KEY = 'mfaSessionId';

export function checkHeadersForMFARequired(e: FetchError): boolean {
  return !!(e.response.headers.get('www-authenticate') && (e.response.status === 401 || e.response.status === 403));
}

/**
 * Handles the authentication code flow when a request requires MFA authentication.
 * This function manages the event-driven communication between the HTTP layer and the UI
 * to handle two-factor authentication flows.
 *
 * @param e - The FetchError that triggered the auth code flow
 * @param options - The original request options
 * @param retryRequestOnceValidated - Function to retry the request after successful authentication
 * @returns Promise that resolves to the retried request result or rejects with the original error
 */
export async function handleMFAFlow<TResponse = any | null>({
  e,
  options,
  retryRequestOnceValidated,
}: {
  e: FetchError;
  options: RequestOptions;
  retryRequestOnceValidated: (options: RequestOptions) => Promise<TResponse>;
}): Promise<TResponse | null> {
  const eventConsumed = await listenerPromise<boolean>({
    eventType: CustomEventType.MFA_REQUIRED_CONSUMED,
    // expect it to be resolve within 100ms
    timeoutDuration: 100,
    // Send an event to initiate the authcode flow, providing the request and url for replication
    onInit: () => document.dispatchEvent(new CustomEvent(CustomEventType.MFA_REQUIRED)),
  });

  if (eventConsumed.error) {
    return Promise.reject(e);
  }

  // Wait for the authentication result event
  const authenticationResult = await listenerPromise<MFAResultEventDetails>({
    eventType: CustomEventType.MFA_RESULT,
    // do no timeout.
    timeoutDuration: null,
  });

  // authentication flow was dismissed
  if (authenticationResult.error || authenticationResult.res.result === 'dismissed') {
    sessionStorage.removeItem(MFA_SESSION_STORAGE_KEY);
    return null;
  }
  // authentication flow failed, propagate the error
  if (authenticationResult.res.result === 'failure') {
    return Promise.reject(e);
  }

  const mfaSessionId = JSON.parse(sessionStorage.getItem(MFA_SESSION_STORAGE_KEY) ?? '""');
  if (authenticationResult.res.result === 'success' && mfaSessionId) {
    // Retry the request with the auth code header
    options.headers = {
      ...(options.headers || {}),
      [MFA_SESSION_HEADER]: mfaSessionId,
    };

    // Wait for 500ms before retrying the request,
    // since backend needs to persist the session
    await new Promise(resolve => setTimeout(resolve, 500));

    return retryRequestOnceValidated(options);
  }

  return Promise.reject(e);
}

function listenerPromise<T>({
  eventType,
  timeoutDuration,
  onInit,
}: {
  eventType: CustomEventType;
  timeoutDuration: number | null;
  onInit?: () => void;
}): Promise<{ error: true } | { res: T; error: false }> {
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const listener = (event: CustomEvent) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener(eventType, listener);
      resolve({ res: event.detail, error: false });
    };
    document.addEventListener(eventType, listener);
    if (timeoutDuration !== null) {
      timeoutId = setTimeout(() => {
        document.removeEventListener(eventType, listener);
        resolve({ error: true });
      }, timeoutDuration);
    }
    onInit?.();
  });
}
